import { useMutation } from '@apollo/client'
import { graphql } from '~publish/gql'
import { useCallback } from 'react'
import { toast } from '@buffer-mono/popcorn'
import { logError } from '~publish/legacy/utils/logError'

export const UpdateChannelGroupMutation = graphql(/* GraphQL */ `
  mutation updateChannelGroup($input: UpdateChannelGroupInput!) {
    updateChannelGroup(input: $input) {
      __typename
      ... on ChannelGroup {
        id
        name
        channels {
          id
          name
        }
      }
      ... on UnauthorizedError {
        message
      }
      ... on UnexpectedError {
        message
      }
    }
  }
`)

interface UseUpdateChannelGroupReturn {
  updateChannelGroup: ({
    channelGroupId,
    name,
    channelIds,
  }: {
    channelGroupId: string
    name: string
    channelIds: string[]
  }) => Promise<void>
  loading: boolean
}

/**
 * Custom hook to update a channel group.
 * @returns {UseUpdateChannelGroupReturn} The function to trigger updating a channel group and the loading state.
 */
export const useUpdateChannelGroup = (): UseUpdateChannelGroupReturn => {
  const [executeUpdateChannelGroup, { loading }] = useMutation(
    UpdateChannelGroupMutation,
  )

  const updateChannelGroup = useCallback(
    async ({
      channelGroupId,
      name,
      channelIds,
    }: {
      channelGroupId: string
      name: string
      channelIds: string[]
    }) => {
      if (!channelGroupId) {
        throw new Error('Channel group ID is required')
      }

      try {
        const { data, errors } = await executeUpdateChannelGroup({
          variables: {
            input: {
              id: channelGroupId,
              name,
              channelIds,
            },
          },
          update: (cache, { data: mutationData }) => {
            if (
              mutationData?.updateChannelGroup?.__typename === 'ChannelGroup'
            ) {
              const updatedChannelGroup = mutationData.updateChannelGroup

              // Update the specific channel group using cache.identify
              cache.modify({
                id: cache.identify({
                  __typename: updatedChannelGroup.__typename,
                  id: String(updatedChannelGroup.id),
                }),
                fields: {
                  name: () => updatedChannelGroup.name,
                  channels: (_, { toReference }) => {
                    return (
                      updatedChannelGroup.channels?.map((channel) => {
                        return toReference({
                          __typename: 'Channel',
                          id: channel.id,
                        })
                      }) || []
                    )
                  },
                },
              })
            }
          },
        })

        if (data?.updateChannelGroup) {
          toast.success('Channel group updated successfully!')
        }

        if (errors?.length) {
          toast.error('Failed to update channel group')
          const unexpectedError = new Error(
            errors?.[0]?.message || 'Unexpected API response',
          )
          logError(unexpectedError, { metaData: { data, errors } })
        }
      } catch (e) {
        toast.error('Failed to update channel group')
        logError(e instanceof Error ? e : new Error(String(e)), {
          metaData: { variables: { channelGroupId } },
        })
      }
    },
    [executeUpdateChannelGroup],
  )

  return {
    updateChannelGroup,
    loading,
  }
}
